// ===============================
// 1. Interface
// ===============================

export interface FieldScheme {
  fields: string[];
  pattern: keyof typeof Patterns;
  required: boolean;
  label?: string;
  // true = file excel CÓ THỂ KHÔNG CÓ cột này
  isColumnOptional?: boolean;
}

export interface ValidationMapping extends FieldScheme {
  key: string;
}

export interface TemplateFileConfig {
  label?: string;
  getUrl: (activeFID?: number) => string;
}

export interface ValidationTypeConfig {
  requiredCols: string[][];
  mapping: ValidationMapping[];
  templateFile?: TemplateFileConfig;
}

// ===============================
// 2. Patterns
// ===============================

export const Patterns = {
  email: (v: any) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim()),

  phone: (v: any) =>
    /^(0|\+84)[0-9]{9}$/.test(String(v || "").replace(/[\s.-]/g, "")),

  text: (v: any) =>
    String(v || "").trim().length > 0,

  username: (v: any) =>
    /^[a-z0-9._-]+$/.test(String(v || "").trim()),

  password: (v: any) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(
      String(v || "")
    ),

  role: (v: any) =>
    ["1", "2", "3", 1, 2, 3].includes(v)
};

// ===============================
// 3. Field schemes
// ===============================

const FIELD_SCHEMES: Record<string, FieldScheme> = {
  fullname: {
    label: "HỌ VÀ TÊN",
    fields: ["HỌ TÊN", "HỌ VÀ TÊN", "FULLNAME", "NAME"],
    pattern: "text",
    required: true
  },

  email: {
    label: "EMAIL",
    fields: ["EMAIL"],
    pattern: "email",
    required: true
  },

  phone: {
    label: "SỐ ĐIỆN THOẠI",
    fields: ["SỐ ĐIỆN THOẠI", "PHONE", "SDT"],
    pattern: "phone",
    required: true
  },

  address: {
    label: "ĐỊA CHỈ",
    fields: ["ĐỊA CHỈ", "ADDRESS"],
    pattern: "text",
    required: false,
    isColumnOptional: true
  },

  // ===== USER FIELDS =====

  username: {
    label: "TÊN ĐĂNG NHẬP",
    fields: ["TÊN ĐĂNG NHẬP", "USERNAME", "LOGIN NAME"],
    pattern: "username",
    required: true
  },

  password: {
    label: "MẬT KHẨU",
    fields: ["MẬT KHẨU", "PASSWORD"],
    pattern: "password",
    required: true
  },

  role: {
    label: "VAI TRÒ",
    fields: ["VAI TRÒ", "ROLE"],
    pattern: "role",
    required: true
  },

  // ===== CONTACT EXTRA FIELDS (OPTIONAL THỰC SỰ) =====

  title: {
    label: "CHỨC DANH",
    fields: ["CHỨC DANH", "TITLE", "POSITION"],
    pattern: "text",
    required: false,
    isColumnOptional: true
  },

  department: {
    label: "PHÒNG BAN",
    fields: ["PHÒNG BAN", "DEPARTMENT"],
    pattern: "text",
    required: false,
    isColumnOptional: true
  },

  organization: {
    label: "ĐƠN VỊ",
    fields: ["ĐƠN VỊ", "ORGANIZATION", "COMPANY"],
    pattern: "text",
    required: false,
    isColumnOptional: true
  },

  code: {
    label: "MÃ NHẬN DẠNG",
    fields: ["CODE", "MÃ NHẬN DẠNG"],
    pattern: "text",
    required: false,
    isColumnOptional: true
  }
};

// ===============================
// 4. Validation schema
// ===============================

const VALIDATION_SCHEMES: Record<string, string[]> = {
  contact: [
    "fullname",
    "email",
    "phone",
    "title",
    "department",
    "organization",
    "code"
  ],
  user: [
    "fullname",
    "username",
    "password",
    "phone",
    "email",
    "role"
  ],
  meeting: ["fullname", "email", "phone"]
};

// ===============================
// 5. Template file config
// ===============================

const TEMPLATE_FILES: Record<string, TemplateFileConfig> = {
  contact: {
    label: "File mẫu Contact",
    getUrl: (activeFID?: number) =>
      activeFID === 1
        ? "static/import_contact_template_fid.xlsx"
        : "static/import_contact_template.xlsx"
  },
  user: {
    label: "File mẫu User",
    getUrl: () => "static/import_user_template.xlsx"
  },
  meeting: {
    label: "File mẫu Meeting",
    getUrl: () => "static/import_user_meeting_template.xlsx"
  }
};

// ===============================
// 6. Build VALIDATION_CONFIG
// ===============================

export const VALIDATION_CONFIG: Record<string, ValidationTypeConfig> = {};

Object.keys(VALIDATION_SCHEMES).forEach(type => {
  const fieldKeys = VALIDATION_SCHEMES[type];

  const mapping: ValidationMapping[] = fieldKeys.map(key => {
    const scheme = FIELD_SCHEMES[key];

    return {
      key,
      ...scheme,
      // fallback an toàn: required=false ⇒ optional
      isColumnOptional:
        scheme.isColumnOptional ?? !scheme.required,
      fields: scheme.fields.map(f => f.trim().toUpperCase())
    };
  });

  VALIDATION_CONFIG[type] = {
    // requiredCols = những cột BẮT BUỘC PHẢI CÓ TRONG FILE
    requiredCols: mapping
      .filter(m => !m.isColumnOptional)
      .map(m => m.fields),

    mapping,
    templateFile: TEMPLATE_FILES[type]
  };
});
